import { jsxs, jsx } from 'react/jsx-runtime';
import { client } from 'braintree-web';
import classNames from 'classnames';
import { forwardRef, useState, useEffect, useImperativeHandle } from 'react';
import { LockMid } from '@zooplus/icons';
import { Alert, Label, FormFieldWrap, Checkbox } from '@zooplus/ui-components';
import CardExpiryDateField from '../CardExpiryDateField/CardExpiryDateField.js';
import CardIcon from '../CardIcon/CardIcon.js';
import CardNumberFormField from '../CardNumberFormField/CardNumberFormField.js';
import CreditCardContentIcon from '../CreditCardContentIcon/CreditCardContentIcon.js';
import CVVIcon from '../CVVIcon/CVVIcon.js';
import DetectedCardBrandsController from '../DetectedCardBrandsController/DetectedCardBrandsController.js';
import EncryptedFormField from '../EncryptedFormField/EncyrptedFormField.js';
import Message from '../Message/Message.js';
import useBraintreeState from '../../hooks/useBraintreeState.js';
import { useTranslation } from '../../hooks/useTranslations.js';
import { useConfig } from '../../providers/ConfigProvider.js';
import { useErrorContext } from '../../providers/ErrorContextProvider.js';
import { sanitizeCardType } from '../../services/braintreeService.js';
import { captureException } from '../../services/sentryService.js';
import { creditCardBrandOfCardType } from '../../types-internal/index.js';
import { getBraintreeStatusCallbacks } from '../../utils/braintreeUtils.js';
import styles from './BraintreeCreditCardFormNew.module.scss.js';

const DISPLAYABLE_CARD_TYPES = [
    'visa',
    'mastercard',
    'diners-club',
    'american-express',
    'maestro',
];
const BraintreeCreditCardFormNew = forwardRef(({ token, containerType, error, allowVaulting = false, onFatalError }, forwardedRef) => {
    const { t } = useTranslation();
    const { setError } = useErrorContext();
    const { config: { env }, } = useConfig();
    const isLocalEnv = env === 'local';
    const [storeInVault, setStoreInVault] = useState(false);
    const { braintreeInstance, intialized, fieldsValidity, fieldsEmpty, supportedCardTypes, hostedFieldsCards, createHostedFields, tokenizeCard, isCurrentCardNumberSupported, handleFatalError, } = useBraintreeState(onFatalError, containerType, storeInVault, isLocalEnv);
    const commonCards = hostedFieldsCards.filter((element) => supportedCardTypes.includes(sanitizeCardType(element.type)));
    const selectedCardType = commonCards.length == 1 ? sanitizeCardType(commonCards[0].type) : null;
    const { isInvalid, isInvalidEmpty, isCardTypeActive } = getBraintreeStatusCallbacks(intialized, fieldsValidity, hostedFieldsCards, supportedCardTypes, fieldsEmpty);
    //check we have same function in useBraintreeState
    useEffect(() => {
        (async () => {
            try {
                if (isLocalEnv)
                    console.log('Creating new braintree instance...');
                braintreeInstance.current = await client.create({ authorization: token });
            }
            catch (error) {
                // error.code = 'INSTANTIATION_OPTION_REQUIRED' when token is not set
                // error.code = 'CLIENT_AUTHORIZATION_INVALID' when token is not valid
                handleFatalError(error);
                captureException({ error, message: 'Cannot create Braintree client' });
                throw error;
            }
            createHostedFields();
        })();
    }, [createHostedFields, handleFatalError, token]);
    // Hide message when validity changed back to all valid
    useEffect(() => {
        if (Object.values(fieldsValidity).every((v) => v)) {
            setError(undefined);
        }
    }, [fieldsValidity, setError]);
    // For calling from within Ract application
    useImperativeHandle(forwardedRef, () => ({
        tokenizeCard,
    }));
    const handleStoreInVaultChange = (event) => {
        setStoreInVault(event.target.checked);
    };
    const isAmex = hostedFieldsCards.length === 1 && isCardTypeActive('american-express');
    const isSupported = isCurrentCardNumberSupported();
    return (jsxs("div", { "data-zta": "braintree-cc-form-container", children: [error && (jsx(Alert, { className: styles.errorMessage, variant: "warning", children: error })), jsxs("div", { className: styles.braintreeCreditCardForm, "data-zta": "braintree-cc-form", children: [jsx("div", { className: styles.secureFormMessage, children: jsx(Message, { color: "grey", text: t('checkout.payment.secure'), icon: jsx(LockMid, {}) }) }), jsxs("div", { className: styles.ccNumberInputContainer, children: [jsx(Label, { children: t('checkout.payment.creditcard.card_number') }), jsx("div", { className: styles.activeCards, children: !selectedCardType &&
                                    DISPLAYABLE_CARD_TYPES.filter((type, supported) => supportedCardTypes.includes(type)).map((type) => {
                                        return (jsx(CardIcon, { type: type, className: classNames(!isCardTypeActive(type) && styles.inactiveCard) }, type));
                                    }) })] }), jsx(CardNumberFormField, { detectedBrandsController: selectedCardType ? (jsx(DetectedCardBrandsController, { selectedBrand: creditCardBrandOfCardType[selectedCardType], coBrandedMessageClassName: styles.coBrandedMessageContainer })) : (jsx(CreditCardContentIcon, {})), id: 'cc-number', errorMessage: isInvalidEmpty('number')
                            ? t('checkout.form.required.error')
                            : !isSupported
                                ? t('checkout.payment.creditcard.type_not_supported')
                                : isInvalid('number')
                                    ? t('checkout.payment.creditcard.error.form.cardnumber.invalid')
                                    : null, hasInfoMessage: false, hasLabel: false }), jsx(EncryptedFormField, { id: "cardholder-name", labelText: t('checkout.payment.creditcard.holder_name'), errorMessage: isInvalidEmpty('cardholderName') ? t('checkout.form.required.error') : null, "data-zta": "inputCardName" }), jsx(CardExpiryDateField, { errorMessage: isInvalidEmpty('expirationYear') || isInvalidEmpty('expirationMonth')
                            ? t('checkout.form.required.error')
                            : isInvalid('expirationYear') || isInvalid('expirationMonth')
                                ? t('checkout.payment.creditcard.expiry_date.invalid')
                                : null }), jsx(EncryptedFormField, { className: styles.cvv, id: "cc-cvv", notFullWidth: true, labelText: isAmex
                            ? t('checkout.payment.creditcard.cid.generic_code')
                            : t('checkout.payment.creditcard.cvc.generic_code'), icon: jsx(CVVIcon, { isAmex: isAmex }), infoMessage: isAmex
                            ? t('checkout.payment.creditcard.cid.warning')
                            : t('checkout.payment.creditcard.cvc.warning'), errorMessage: isInvalid('cvv')
                            ? t('checkout.form.cvc.min.length.error', { '0': isAmex ? 4 : 3 })
                            : isInvalidEmpty('cvv')
                                ? t('checkout.form.required.error')
                                : null, "data-zta": "inputCardCode" }), allowVaulting && (jsx(FormFieldWrap, { labelText: "", children: jsx(Checkbox, { className: styles.vaultCheck, id: "vault-check", checked: storeInVault, onChange: handleStoreInVaultChange, "data-zta": "storeCreditCard", children: t('checkout.payment.save.creditcard') }) }))] })] }));
});

export { BraintreeCreditCardFormNew as default };
//# sourceMappingURL=BraintreeCreditCardFormNew.js.map
